import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Grid, Card, CardMedia, Skeleton, Button, Paper, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { getFavoriteAnime, addFavoriteAnime, removeFavoriteAnime, reorderFavoriteAnime, FavoriteAnime } from '../../../services/animeApi';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SortableGameGrid } from '../../common/SortableGameGrid';
import AnimeSelectDialog, { AnimeSelectResult } from '../../dialogs/AnimeSelectDialog';

const MAX_FAVORITES = 16;
const VISIBLE_COUNT = 8;

interface FavoriteAnimeWidgetProps {
  username?: string;
}

const getCoverUrl = (imageUrl: string | null) => {
  if (!imageUrl) return '/image/no_image.png';
  return imageUrl;
};

const EmptySlot: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      position: 'relative',
      paddingTop: '133%',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {},
    }}
  >
    {onClick && (
      <IconButton
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'text.secondary',
        }}
      >
        <AddIcon />
      </IconButton>
    )}
  </Card>
);

const commonPaperStyle = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'background.paper',
  backdropFilter: 'blur(10px)',
  position: 'relative',
};

const FavoriteAnimeWidget: React.FC<FavoriteAnimeWidgetProps> = ({ username }) => {
  const [favorites, setFavorites] = useState<FavoriteAnime[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingFavorites, setEditingFavorites] = useState<FavoriteAnime[]>([]);
  const [expanded, setExpanded] = useState(false);
  const expandRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFavoriteAnime(username);
      setFavorites(data);
    } catch (error) {
      console.error('お気に入りアニメの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  useEffect(() => {
    if (editMode) setEditingFavorites([...favorites]);
  }, [editMode]);

  useEffect(() => {
    if (expandRef.current) setContentHeight(expandRef.current.scrollHeight);
  }, [favorites, editMode, editingFavorites]);

  const handleAddAnime = async (animeList: AnimeSelectResult[]) => {
    try {
      const startOrder = favorites.length;
      const newFavorites: FavoriteAnime[] = [];
      for (let i = 0; i < animeList.length; i++) {
        const newFav = await addFavoriteAnime(animeList[i].mal_id, startOrder + i);
        newFavorites.push(newFav);
      }
      setFavorites(prev => [...prev, ...newFavorites].sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('お気に入りアニメの追加に失敗しました:', error);
    }
  };

  const handleDelete = (malId: number) => {
    setEditingFavorites(prev => prev.filter(f => f.mal_id !== malId));
  };

  const handleReorder = (newItems: any[]) => {
    setEditingFavorites(prev =>
      newItems.map((item, index) => ({
        ...prev.find(f => f.mal_id === item.id)!,
        order: index,
      }))
    );
  };

  const handleEditComplete = async () => {
    try {
      const deletedItems = favorites.filter(
        f => !editingFavorites.some(ef => ef.mal_id === f.mal_id)
      );
      const reorderedItems = editingFavorites.map((item, index) => ({
        id: item.mal_id,
        order: index,
      }));

      for (const item of deletedItems) {
        await removeFavoriteAnime(item.mal_id);
      }
      if (reorderedItems.length > 0) {
        await reorderFavoriteAnime(reorderedItems);
      }

      setFavorites(editingFavorites);
      setEditMode(false);
    } catch (error) {
      console.error('お気に入りの更新に失敗しました:', error);
      setEditingFavorites([...favorites]);
    }
  };

  const visibleFavorites = favorites.slice(0, VISIBLE_COUNT);
  const extraFavorites = favorites.slice(VISIBLE_COUNT);
  const showExpandButton = !editMode && (extraFavorites.length > 0 || (!username && favorites.length >= VISIBLE_COUNT));
  const hasExtraContent = extraFavorites.length > 0;

  if (loading) {
    return (
      <Paper elevation={0} sx={commonPaperStyle}>
        <Typography variant="subtitle2" color="text.secondary">ベストアニメ</Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {[...Array(VISIBLE_COUNT)].map((_, i) => (
            <Grid item xs={3} sm={3} md={1.5} key={i}>
              <Box sx={{ position: 'relative', paddingTop: '133%' }}>
                <Skeleton variant="rectangular" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 1 }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{
      ...commonPaperStyle,
      p: { xs: 2, sm: 2.5 },
      ...(showExpandButton && !hasExtraContent && !expanded && { pb: { xs: 0.5, sm: 1 } }),
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" lineHeight={1}>
          {username ? `@${username}のベストアニメ` : 'ベストアニメ'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!username && favorites.length > 0 && (
            editMode ? (
              <>
                <Button startIcon={<CloseIcon />} size="small" variant="outlined" onClick={() => setEditMode(false)} color="error">キャンセル</Button>
                <Button startIcon={<DoneIcon />} size="small" variant="outlined" onClick={handleEditComplete} color="success">完了</Button>
              </>
            ) : (
              <Button startIcon={<EditIcon />} size="small" variant="outlined" onClick={() => { setEditMode(true); setExpanded(true); }} color="primary" sx={{ height: '28px' }}>編集</Button>
            )
          )}
        </Box>
      </Box>

      {editMode && (
        <Box sx={{ p: 2, mb: 2, backgroundColor: 'rgba(25, 118, 210, 0.08)', border: '1px solid rgba(25, 118, 210, 0.2)', borderRadius: 1 }}>
          <Typography variant="body2" color="primary">タイトルの削除と、ドラッグ＆ドロップで並び替えができます</Typography>
        </Box>
      )}

      {editMode ? (
        <SortableGameGrid
          items={editingFavorites.map(f => ({
            id: f.mal_id,
            name: f.title_japanese || f.title,
            cover_url: getCoverUrl(f.image_url),
            order: f.order,
          }))}
          onReorder={handleReorder}
          onDelete={handleDelete}
          maxItems={MAX_FAVORITES}
          gridTemplateColumns={{ xs: 'repeat(4, 1fr)', sm: 'repeat(8, 1fr)' }}
        />
      ) : (
        <>
          {favorites.length === 0 && !username ? (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={3} sm={3} md={1.5}>
                <EmptySlot onClick={() => setSearchOpen(true)} />
              </Grid>
            </Grid>
          ) : favorites.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">まだお気に入りアニメが登録されていません</Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                {visibleFavorites.map((fav) => (
                  <Grid item xs={3} sm={3} md={1.5} key={fav.id}>
                    <Link to={`/anime/${fav.mal_id}`} style={{ textDecoration: 'none' }}>
                      <Card sx={{ position: 'relative', paddingTop: '133%', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
                        <CardMedia
                          component="img"
                          image={getCoverUrl(fav.image_url)}
                          alt={fav.title_japanese || fav.title}
                          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
                        />
                      </Card>
                    </Link>
                  </Grid>
                ))}
                {!username && favorites.length < VISIBLE_COUNT && [...Array(VISIBLE_COUNT - favorites.length)].map((_, index) => (
                  <Grid item xs={3} sm={3} md={1.5} key={`empty-${index}`}>
                    <EmptySlot onClick={() => setSearchOpen(true)} />
                  </Grid>
                ))}
              </Grid>

              {showExpandButton && (
                <Box sx={{ position: 'relative', mt: { xs: 1, sm: 1.5 } }}>
                  <Box
                    ref={expandRef}
                    sx={{
                      maxHeight: expanded ? `${contentHeight + 30}px` : hasExtraContent ? { xs: '60px', sm: '70px' } : '0px',
                      overflow: 'hidden',
                      transition: expanded
                        ? 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        : 'max-height 0.4s cubic-bezier(0.4, 0, 0.6, 1)',
                    }}
                  >
                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                      {extraFavorites.map((fav) => (
                        <Grid item xs={3} sm={3} md={1.5} key={fav.id}>
                          <Link to={`/anime/${fav.mal_id}`} style={{ textDecoration: 'none', pointerEvents: expanded ? 'auto' : 'none' }}>
                            <Card sx={{ position: 'relative', paddingTop: '133%', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
                              <CardMedia component="img" image={getCoverUrl(fav.image_url)} alt={fav.title_japanese || fav.title}
                                sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }} />
                            </Card>
                          </Link>
                        </Grid>
                      ))}
                      {!username && favorites.length >= VISIBLE_COUNT && [...Array(MAX_FAVORITES - Math.max(favorites.length, VISIBLE_COUNT))].map((_, index) => (
                        <Grid item xs={3} sm={3} md={1.5} key={`empty-extra-${index}`}>
                          <EmptySlot onClick={() => setSearchOpen(true)} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  <Box
                    onClick={() => setExpanded(!expanded)}
                    sx={{
                      position: hasExtraContent ? 'absolute' : 'relative',
                      bottom: hasExtraContent ? 0 : 'auto',
                      left: 0, right: 0,
                      height: expanded ? 0 : hasExtraContent ? { xs: '90px', sm: '70px' } : '32px',
                      background: hasExtraContent
                        ? (theme: any) => `linear-gradient(to bottom, transparent 0%, ${theme.palette.background.paper}e6 70%, ${theme.palette.background.paper} 100%)`
                        : 'none',
                      cursor: 'pointer',
                      transition: expanded ? 'opacity 0.2s ease-out, height 0s 0.2s' : 'opacity 0.3s 0.15s ease-in, height 0s 0s',
                      opacity: expanded ? 0 : 1,
                      display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 0.5,
                    }}
                  >
                    {!hasExtraContent && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.5, fontSize: '0.7rem', mr: 0.5 }}>拡張可能</Typography>
                    )}
                    <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', opacity: expanded ? 0 : 0.7 }} />
                  </Box>

                  {expanded && (
                    <Box onClick={() => setExpanded(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1, py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
                      <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', transform: 'rotate(180deg)' }} />
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </>
      )}
      <AnimeSelectDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onMultiSelect={handleAddAnime}
        multiSelect
        maxSelect={MAX_FAVORITES - favorites.length}
        title="お気に入りアニメを追加"
        excludeMalIds={favorites.map(f => f.mal_id)}
      />
    </Paper>
  );
};

export default FavoriteAnimeWidget;
